import { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Clock, Link2 } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { BoardWithLists, BoardCard, CardDependency } from '@/types'
import { boardsApi, cardsApi } from '@/lib/api'
import {
  format,
  parseISO,
  differenceInDays,
  addDays,
  startOfDay,
  endOfDay,
  eachDayOfInterval,
  isWeekend,
  isSameDay,
  addWeeks,
  subWeeks,
  startOfWeek,
} from 'date-fns'

type ZoomLevel = 'day' | 'week' | 'month'
type DragType = 'move' | 'resize-start' | 'resize-end'

interface GanttViewProps {
  board: BoardWithLists
  boardId: string
  onCardClick: (card: BoardCard) => void
}

interface GanttTask extends BoardCard {
  listName: string
  listColor?: string
}

interface DragState {
  task: GanttTask
  type: DragType
  startX: number
  initialStartDate: Date
  initialEndDate: Date
  currentOffset: number
}

export default function GanttView({ board, boardId, onCardClick }: GanttViewProps) {
  const queryClient = useQueryClient()
  const [currentDate, setCurrentDate] = useState(new Date())
  const [zoomLevel, setZoomLevel] = useState<ZoomLevel>('week')
  const [dependencies, setDependencies] = useState<CardDependency[]>([])
  const [dragState, setDragState] = useState<DragState | null>(null)
  const [isUpdating, setIsUpdating] = useState(false)
  const [hasDragged, setHasDragged] = useState(false)
  const ganttRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)

  // Fetch dependencies for the board
  useEffect(() => {
    const fetchDependencies = async () => {
      try {
        const deps = await boardsApi.getDependencies(boardId)
        setDependencies(deps || [])
      } catch (error) {
        console.error('Failed to fetch dependencies:', error)
      }
    }
    fetchDependencies()
  }, [boardId])

  // Get all tasks with dates
  const tasks = useMemo(() => {
    const allTasks: GanttTask[] = []
    board.lists.forEach(list => {
      list.cards?.forEach(card => {
        if (card.start_date || card.due_date) {
          allTasks.push({
            ...card,
            listName: list.list_name,
            listColor: list.list_color,
          })
        }
      })
    })
    // Sort by start date, then by due date
    return allTasks.sort((a, b) => {
      const aStart = a.start_date ? parseISO(a.start_date) : a.due_date ? parseISO(a.due_date) : new Date()
      const bStart = b.start_date ? parseISO(b.start_date) : b.due_date ? parseISO(b.due_date) : new Date()
      return aStart.getTime() - bStart.getTime()
    })
  }, [board.lists])

  // Calculate date range for the Gantt chart
  const dateRange = useMemo(() => {
    const daysToShow = zoomLevel === 'day' ? 14 : zoomLevel === 'week' ? 28 : 90
    const start = startOfWeek(currentDate)
    const end = addDays(start, daysToShow - 1)
    return { start, end, days: eachDayOfInterval({ start, end }) }
  }, [currentDate, zoomLevel])

  // Column width based on zoom level
  const columnWidth = zoomLevel === 'day' ? 60 : zoomLevel === 'week' ? 40 : 20
  const rowHeight = 40
  const headerHeight = 60
  const taskListWidth = 380

  const navigate = (direction: 'prev' | 'next') => {
    const weeks = zoomLevel === 'month' ? 4 : zoomLevel === 'week' ? 2 : 1
    setCurrentDate(direction === 'prev' ? subWeeks(currentDate, weeks) : addWeeks(currentDate, weeks))
  }

  const goToToday = () => setCurrentDate(new Date())

  // Get hours per day from board settings (default to 8)
  const hoursPerDay = board.hours_per_day || 8

  // Calculate duration in days from effort (hours)
  const getEffortBasedDuration = (estimatedHours?: number): number | null => {
    if (!estimatedHours || estimatedHours <= 0) return null
    return estimatedHours / hoursPerDay
  }

  // Format duration for display (supports fractional days)
  // Priority: effort-based duration if effort exists, otherwise date-based
  const formatDuration = (task: GanttTask): string => {
    // If effort is provided, always use effort-based duration
    const effortDays = getEffortBasedDuration(task.estimated_hours)
    if (effortDays !== null) {
      if (effortDays < 1) {
        return `${(effortDays * hoursPerDay).toFixed(0)}h`
      }
      return effortDays === Math.floor(effortDays) ? `${effortDays}d` : `${effortDays.toFixed(1)}d`
    }

    // Fall back to date-based calculation if no effort
    const startDate = task.start_date ? parseISO(task.start_date) : null
    const endDate = task.due_date ? parseISO(task.due_date) : null
    if (startDate && endDate) {
      const days = differenceInDays(endDate, startDate) + 1
      return `${days}d`
    }

    return '1d'
  }

  const getTaskPosition = (task: GanttTask) => {
    const startDate = task.start_date ? parseISO(task.start_date) : task.due_date ? parseISO(task.due_date) : null
    let endDate = task.due_date ? parseISO(task.due_date) : startDate

    if (!startDate) return null

    const startOffset = differenceInDays(startOfDay(startDate), dateRange.start)

    // Calculate duration - prioritize effort-based if effort exists
    let duration: number
    const effortDays = getEffortBasedDuration(task.estimated_hours)
    if (effortDays !== null) {
      // Use effort-based duration (minimum 0.25 days = 2 hours for visibility)
      duration = Math.max(0.25, effortDays)
    } else if (endDate) {
      // Fall back to date-based duration
      duration = Math.max(1, differenceInDays(endOfDay(endDate), startOfDay(startDate)) + 1)
    } else {
      duration = 1
    }

    return {
      left: Math.max(0, startOffset * columnWidth),
      width: Math.max(duration * columnWidth - 4, 20), // Minimum width of 20px
      isVisible: startOffset + Math.ceil(duration) > 0 && startOffset < dateRange.days.length,
    }
  }

  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case 'URGENT': return 'bg-red-500'
      case 'HIGH': return 'bg-orange-500'
      case 'MEDIUM': return 'bg-yellow-500'
      case 'LOW': return 'bg-green-500'
      default: return 'bg-primary'
    }
  }

  const formatEstimatedHours = (hours?: number) => {
    if (!hours) return null
    if (hours < 1) return `${Math.round(hours * 60)}m`
    if (hours === Math.floor(hours)) return `${hours}h`
    return `${hours.toFixed(1)}h`
  }

  // Calculate total effort
  const totalEffort = useMemo(() => {
    return tasks.reduce((sum, task) => sum + (task.estimated_hours || 0), 0)
  }, [tasks])

  // Drag handlers for task bars
  const handleDragStart = useCallback((e: React.MouseEvent, task: GanttTask, type: DragType) => {
    e.preventDefault()
    e.stopPropagation()

    const startDate = task.start_date ? parseISO(task.start_date) : task.due_date ? parseISO(task.due_date) : new Date()

    // Calculate end date: use due_date if available, otherwise calculate from effort
    let endDate: Date
    if (task.due_date) {
      endDate = parseISO(task.due_date)
    } else if (task.estimated_hours && task.estimated_hours > 0) {
      // Calculate end date from effort: effort / hours_per_day = duration in days
      const effortDays = task.estimated_hours / hoursPerDay
      const durationDays = Math.max(1, Math.ceil(effortDays)) - 1 // -1 because start day counts as day 1
      endDate = addDays(startDate, durationDays)
    } else {
      endDate = startDate
    }

    setDragState({
      task,
      type,
      startX: e.clientX,
      initialStartDate: startOfDay(startDate),
      initialEndDate: startOfDay(endDate),
      currentOffset: 0,
    })
  }, [hoursPerDay])

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!dragState) return

    const deltaX = e.clientX - dragState.startX
    const dayOffset = Math.round(deltaX / columnWidth)

    if (dayOffset !== dragState.currentOffset) {
      setDragState(prev => prev ? { ...prev, currentOffset: dayOffset } : null)
      // Mark that we've actually dragged (moved at least one day)
      if (dayOffset !== 0) {
        setHasDragged(true)
      }
    }
  }, [dragState, columnWidth])

  const handleMouseUp = useCallback(async () => {
    if (!dragState || dragState.currentOffset === 0) {
      setDragState(null)
      // Reset hasDragged after a short delay to allow click handler to check it
      setTimeout(() => setHasDragged(false), 100)
      return
    }

    const { task, type, currentOffset, initialStartDate, initialEndDate } = dragState
    setDragState(null)
    setHasDragged(true) // Keep hasDragged true during update
    setIsUpdating(true)

    try {
      let newStartDate: Date | null = null
      let newEndDate: Date | null = null

      if (type === 'move') {
        newStartDate = addDays(initialStartDate, currentOffset)
        newEndDate = addDays(initialEndDate, currentOffset)
      } else if (type === 'resize-start') {
        newStartDate = addDays(initialStartDate, currentOffset)
        newEndDate = initialEndDate
        // Ensure start doesn't go past end
        if (newStartDate > newEndDate) {
          newStartDate = newEndDate
        }
      } else if (type === 'resize-end') {
        newStartDate = initialStartDate
        newEndDate = addDays(initialEndDate, currentOffset)
        // Ensure end doesn't go before start
        if (newEndDate < newStartDate) {
          newEndDate = newStartDate
        }
      }

      if (newStartDate && newEndDate) {
        // Format dates as YYYY-MM-DD and create UTC midnight timestamps
        // This ensures the date doesn't shift due to timezone conversion
        const formatDateForApi = (date: Date) => {
          const year = date.getFullYear()
          const month = String(date.getMonth() + 1).padStart(2, '0')
          const day = String(date.getDate()).padStart(2, '0')
          return new Date(`${year}-${month}-${day}T00:00:00Z`).toISOString()
        }

        // Calculate new duration in days
        const newDurationDays = differenceInDays(newEndDate, newStartDate) + 1

        // Build update payload
        const updatePayload: { start_date: string; due_date: string; estimated_hours?: number } = {
          start_date: formatDateForApi(newStartDate),
          due_date: formatDateForApi(newEndDate),
        }

        // If resizing (not moving) and task has effort, recalculate effort based on new duration
        if (type !== 'move' && task.estimated_hours) {
          // Recalculate effort: new duration (days) * hours per day
          const newEffort = newDurationDays * hoursPerDay
          updatePayload.estimated_hours = newEffort
        }

        await cardsApi.update(task.card_id, updatePayload)
        queryClient.invalidateQueries({ queryKey: ['board', boardId] })

        if (type !== 'move' && task.estimated_hours) {
          const newEffort = newDurationDays * hoursPerDay
          toast.success(`Task updated: ${newDurationDays}d (${newEffort}h effort)`)
        } else {
          toast.success('Task dates updated')
        }
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update task'
      toast.error(errorMessage)
    } finally {
      setIsUpdating(false)
      // Reset hasDragged after a short delay
      setTimeout(() => setHasDragged(false), 100)
    }
  }, [dragState, boardId, queryClient, hoursPerDay])

  // Add/remove mouse event listeners for drag
  useEffect(() => {
    if (dragState) {
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
      return () => {
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [dragState, handleMouseMove, handleMouseUp])

  // Calculate dragged task position
  const getDraggedTaskPosition = useCallback((task: GanttTask) => {
    if (!dragState || dragState.task.card_id !== task.card_id) {
      return getTaskPosition(task)
    }

    const { type, currentOffset, initialStartDate, initialEndDate } = dragState
    let startDate = initialStartDate
    let endDate = initialEndDate

    if (type === 'move') {
      startDate = addDays(initialStartDate, currentOffset)
      endDate = addDays(initialEndDate, currentOffset)
    } else if (type === 'resize-start') {
      startDate = addDays(initialStartDate, currentOffset)
      if (startDate > endDate) startDate = endDate
    } else if (type === 'resize-end') {
      endDate = addDays(initialEndDate, currentOffset)
      if (endDate < startDate) endDate = startDate
    }

    const startOffset = differenceInDays(startOfDay(startDate), dateRange.start)
    const duration = Math.max(1, differenceInDays(endOfDay(endDate), startOfDay(startDate)) + 1)

    return {
      left: Math.max(0, startOffset * columnWidth),
      width: duration * columnWidth - 4,
      isVisible: startOffset + duration > 0 && startOffset < dateRange.days.length,
    }
  }, [dragState, dateRange, columnWidth, getTaskPosition])

  // Determine cursor style based on drag state
  const getCursorClass = () => {
    if (!dragState) return ''
    if (dragState.type === 'move') return 'cursor-grabbing'
    return 'cursor-ew-resize'
  }

  return (
    <TooltipProvider>
      <div className={`flex-1 flex flex-col p-4 min-h-0 overflow-hidden ${getCursorClass()}`}>
        <div className="bg-card rounded-lg border flex-1 flex flex-col min-h-0 overflow-hidden text-card-foreground">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-semibold">Gantt Chart</h2>
              {isUpdating && (
                <span className="text-sm text-muted-foreground animate-pulse">Updating...</span>
              )}
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" onClick={() => navigate('prev')}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={goToToday}>Today</Button>
                <Button variant="outline" size="icon" onClick={() => navigate('next')}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              <span className="text-sm text-muted-foreground">
                {format(dateRange.start, 'MMM d')} - {format(dateRange.end, 'MMM d, yyyy')}
              </span>
            </div>
            <div className="flex items-center gap-4">
              {/* Zoom controls */}
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={() => setZoomLevel(prev => prev === 'month' ? 'week' : 'day')} disabled={zoomLevel === 'day'}>
                  <ZoomIn className="h-4 w-4" />
                </Button>
                <span className="text-sm capitalize w-12 text-center">{zoomLevel}</span>
                <Button variant="outline" size="icon" onClick={() => setZoomLevel(prev => prev === 'day' ? 'week' : 'month')} disabled={zoomLevel === 'month'}>
                  <ZoomOut className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Gantt Chart Content */}
          <div className="flex-1 flex min-h-0 overflow-hidden">
            {/* Task List (Left Panel) */}
            <div className="flex-shrink-0 border-r" style={{ width: taskListWidth }}>
              {/* Task List Header */}
              <div className="flex items-center border-b bg-muted/50 px-3 font-medium text-sm" style={{ height: headerHeight }}>
                <span className="flex-1">Task</span>
                <span className="w-24 text-center">Assignee</span>
                <span className="w-16 text-center">Effort</span>
                <span className="w-20 text-center">Duration</span>
              </div>
              {/* Task List Items */}
              <div className="overflow-y-auto" style={{ height: `calc(100% - ${headerHeight}px)` }}>
                {tasks.map((task) => {
                  return (
                    <div
                      key={task.card_id}
                      className="flex items-center border-b px-3 hover:bg-muted/30 cursor-pointer"
                      style={{ height: rowHeight }}
                      onClick={() => onCardClick(task)}
                    >
                      <div className="flex-1 flex items-center gap-2 min-w-0">
                        <div
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: task.cover_color || task.listColor || '#6366f1' }}
                        />
                        <span className={`truncate text-sm ${task.is_completed ? 'line-through text-muted-foreground' : ''}`}>
                          {task.card_title}
                        </span>
                        {task.dependencies && task.dependencies.length > 0 && (
                          <Link2 className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                        )}
                      </div>
                      <span className="w-24 text-center text-xs text-muted-foreground truncate" title={task.members?.map(m => m.full_name || m.email).join(', ')}>
                        {task.members && task.members.length > 0
                          ? task.members[0].full_name?.split(' ')[0] || task.members[0].email?.split('@')[0]
                          : '-'}
                        {task.members && task.members.length > 1 && ` +${task.members.length - 1}`}
                      </span>
                      <span className="w-16 text-center text-xs text-muted-foreground">
                        {formatEstimatedHours(task.estimated_hours) || '-'}
                      </span>
                      <span className="w-20 text-center text-xs text-muted-foreground">
                        {formatDuration(task)}
                      </span>
                    </div>
                  )
                })}
                {tasks.length === 0 && (
                  <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
                    No tasks with dates
                  </div>
                )}
              </div>
            </div>

            {/* Timeline (Right Panel) */}
            <div className="flex-1 overflow-auto" ref={ganttRef}>
              <div style={{ width: dateRange.days.length * columnWidth, minWidth: '100%' }}>
                {/* Timeline Header */}
                <div className="sticky top-0 bg-card z-10 border-b" style={{ height: headerHeight }}>
                  {/* Month/Week row */}
                  <div className="flex border-b" style={{ height: headerHeight / 2 }}>
                    {dateRange.days.map((day, index) => {
                      const isFirstOfWeek = day.getDay() === 0
                      const isFirstOfMonth = day.getDate() === 1
                      const showLabel = zoomLevel === 'month' ? isFirstOfMonth : isFirstOfWeek || index === 0

                      return (
                        <div
                          key={day.toISOString()}
                          className="flex-shrink-0 border-r text-xs flex items-center justify-center"
                          style={{ width: columnWidth }}
                        >
                          {showLabel && (
                            <span className="font-medium">
                              {zoomLevel === 'month' ? format(day, 'MMM') : format(day, 'MMM d')}
                            </span>
                          )}
                        </div>
                      )
                    })}
                  </div>
                  {/* Day row */}
                  <div className="flex" style={{ height: headerHeight / 2 }}>
                    {dateRange.days.map((day) => {
                      const isToday = isSameDay(day, new Date())
                      const weekend = isWeekend(day)

                      return (
                        <div
                          key={day.toISOString()}
                          className={`flex-shrink-0 border-r text-xs flex items-center justify-center ${
                            isToday ? 'bg-primary text-primary-foreground font-bold' : weekend ? 'bg-muted/50' : ''
                          }`}
                          style={{ width: columnWidth }}
                        >
                          {zoomLevel !== 'month' && format(day, 'd')}
                          {zoomLevel === 'month' && day.getDate() % 5 === 0 && format(day, 'd')}
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Task Bars */}
                <div className="relative">
                  {/* Grid lines */}
                  <div className="absolute inset-0 flex pointer-events-none">
                    {dateRange.days.map((day) => {
                      const isToday = isSameDay(day, new Date())
                      const weekend = isWeekend(day)

                      return (
                        <div
                          key={day.toISOString()}
                          className={`flex-shrink-0 border-r ${
                            isToday ? 'bg-primary/10' : weekend ? 'bg-muted/30' : ''
                          }`}
                          style={{ width: columnWidth, height: tasks.length * rowHeight }}
                        />
                      )
                    })}
                  </div>

                  {/* Today line */}
                  {dateRange.days.some(d => isSameDay(d, new Date())) && (
                    <div
                      className="absolute top-0 w-0.5 bg-primary z-20"
                      style={{
                        left: differenceInDays(new Date(), dateRange.start) * columnWidth + columnWidth / 2,
                        height: tasks.length * rowHeight,
                      }}
                    />
                  )}

                  {/* Task bars */}
                  {tasks.map((task) => {
                    const position = getDraggedTaskPosition(task)
                    const isDragging = dragState?.task.card_id === task.card_id
                    if (!position || !position.isVisible) {
                      return (
                        <div key={task.card_id} className="border-b" style={{ height: rowHeight }} />
                      )
                    }

                    const hasDependencies = dependencies.some(d => d.card_id === task.card_id)
                    const barHeight = rowHeight - 16

                    return (
                      <div
                        key={task.card_id}
                        className="relative border-b"
                        style={{ height: rowHeight }}
                      >
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div
                              className={`absolute top-2 rounded group flex items-center text-white text-xs font-medium select-none ${
                                task.is_completed ? 'opacity-60' : ''
                              } ${isDragging ? 'opacity-70 shadow-lg z-30' : 'hover:opacity-90'} ${getPriorityColor(task.priority)}`}
                              style={{
                                left: position.left,
                                width: Math.max(position.width, 40),
                                height: barHeight,
                                cursor: isDragging ? 'grabbing' : 'grab',
                                transition: isDragging ? 'none' : 'opacity 0.15s',
                              }}
                              onMouseDown={(e) => handleDragStart(e, task, 'move')}
                              onClick={(e) => {
                                // Only trigger click if not dragging and hasn't just finished dragging
                                if (!dragState && !hasDragged && !isUpdating) {
                                  e.stopPropagation()
                                  onCardClick(task)
                                }
                              }}
                            >
                              {/* Left resize handle */}
                              <div
                                className="absolute left-0 top-0 w-2 h-full cursor-ew-resize opacity-0 group-hover:opacity-100 hover:bg-black/20 rounded-l flex items-center justify-center"
                                onMouseDown={(e) => {
                                  e.stopPropagation()
                                  handleDragStart(e, task, 'resize-start')
                                }}
                              >
                                <div className="w-0.5 h-3 bg-white/50 rounded" />
                              </div>

                              {/* Task content */}
                              <div className="flex-1 flex items-center px-2 min-w-0 overflow-hidden gap-1">
                                <span className="truncate">{task.card_title}</span>
                                {task.members && task.members.length > 0 && (
                                  <span className="text-[10px] opacity-80 truncate flex-shrink-0">
                                    • {task.members[0].full_name?.split(' ')[0] || task.members[0].email?.split('@')[0]}
                                    {task.members.length > 1 && ` +${task.members.length - 1}`}
                                  </span>
                                )}
                                {hasDependencies && <Link2 className="h-3 w-3 ml-1 flex-shrink-0" />}
                              </div>

                              {/* Right resize handle */}
                              <div
                                className="absolute right-0 top-0 w-2 h-full cursor-ew-resize opacity-0 group-hover:opacity-100 hover:bg-black/20 rounded-r flex items-center justify-center"
                                onMouseDown={(e) => {
                                  e.stopPropagation()
                                  handleDragStart(e, task, 'resize-end')
                                }}
                              >
                                <div className="w-0.5 h-3 bg-white/50 rounded" />
                              </div>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs">
                            <div className="space-y-1">
                              <p className="font-medium">{task.card_title}</p>
                              <p className="text-xs text-muted-foreground">{task.listName}</p>
                              {task.members && task.members.length > 0 && (
                                <p className="text-xs">
                                  Assignee: {task.members.map(m => m.full_name || m.email).join(', ')}
                                </p>
                              )}
                              {task.start_date && (
                                <p className="text-xs">Start: {format(parseISO(task.start_date), 'MMM d, yyyy')}</p>
                              )}
                              {task.due_date && (
                                <p className="text-xs">Due: {format(parseISO(task.due_date), 'MMM d, yyyy')}</p>
                              )}
                              {task.estimated_hours && (
                                <p className="text-xs flex items-center gap-1">
                                  <Clock className="h-3 w-3" /> {formatEstimatedHours(task.estimated_hours)}
                                </p>
                              )}
                              <p className="text-xs text-muted-foreground italic">Drag to move • Drag edges to resize</p>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    )
                  })}

                  {/* Dependency arrows (SVG overlay) */}
                  <svg
                    ref={svgRef}
                    className="absolute top-0 left-0 pointer-events-none"
                    style={{ width: dateRange.days.length * columnWidth, height: tasks.length * rowHeight }}
                  >
                    {dependencies.map((dep) => {
                      const fromTask = tasks.find(t => t.card_id === dep.depends_on_card_id)
                      const toTask = tasks.find(t => t.card_id === dep.card_id)
                      if (!fromTask || !toTask) return null

                      const fromIndex = tasks.indexOf(fromTask)
                      const toIndex = tasks.indexOf(toTask)
                      const fromPos = getTaskPosition(fromTask)
                      const toPos = getTaskPosition(toTask)
                      if (!fromPos || !toPos) return null

                      const fromX = fromPos.left + fromPos.width
                      const fromY = fromIndex * rowHeight + rowHeight / 2
                      const toX = toPos.left
                      const toY = toIndex * rowHeight + rowHeight / 2

                      // Create a curved path
                      const midX = (fromX + toX) / 2
                      const path = `M ${fromX} ${fromY} C ${midX} ${fromY}, ${midX} ${toY}, ${toX} ${toY}`

                      return (
                        <g key={dep.dependency_id}>
                          <path
                            d={path}
                            fill="none"
                            stroke="#8b5cf6"
                            strokeWidth="2"
                            strokeDasharray="none"
                            opacity="0.8"
                            markerEnd="url(#arrowhead)"
                          />
                        </g>
                      )
                    })}
                    <defs>
                      <marker
                        id="arrowhead"
                        markerWidth="10"
                        markerHeight="7"
                        refX="9"
                        refY="3.5"
                        orient="auto"
                      >
                        <polygon
                          points="0 0, 10 3.5, 0 7"
                          fill="#8b5cf6"
                        />
                      </marker>
                    </defs>
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Footer with summary */}
          <div className="p-3 border-t flex items-center gap-4 text-xs text-muted-foreground flex-shrink-0">
            <span className="font-medium">Summary:</span>
            <Badge variant="outline">{tasks.length} tasks</Badge>
            <Badge variant="outline">{formatEstimatedHours(totalEffort) || '0h'} total effort</Badge>
            <Badge variant="outline">{dependencies.length} dependencies</Badge>
            <span className="ml-auto flex items-center gap-3">
              <span className="flex items-center gap-1"><div className="w-4 h-0.5 bg-violet-500" /> Dependency</span>
              <span className="border-l pl-3 flex items-center gap-2">
                <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-red-500" /> Urgent</span>
                <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-orange-500" /> High</span>
                <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-yellow-500" /> Medium</span>
                <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-green-500" /> Low</span>
              </span>
            </span>
          </div>
        </div>
      </div>
    </TooltipProvider>
  )
}

